import { useCarePlan, useConditions } from "@repo/data-store";
import type { CarePlanId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { useMemo } from "react";
import { GoalFormFields } from "@/features/cases/components/goal-form-fields";
import { addGoalFromForm, EMPTY_GOAL_FORM, goalFormSchema, useAppForm } from "@/features/cases/forms/use-goal-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";

export interface AddGoalDialogProps {
  carePlanId: CarePlanId | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

/** Dialog: add a goal to a care plan. New goals start in `proposed` status. */
export function AddGoalDialog({ carePlanId, open, onClose, onExited }: AddGoalDialogProps) {
  const carePlan = useCarePlan(carePlanId ?? undefined);
  const { conditions } = useConditions({ patientId: carePlan?.patientId });

  // The goal's addressable conditions are the plan's targeted conditions.
  const conditionOptions = useMemo(() => {
    const planConditionIds = new Set(carePlan?.addresses ?? []);
    return conditions.filter((c) => planConditionIds.has(c.conditionId));
  }, [conditions, carePlan?.addresses]);

  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues: EMPTY_GOAL_FORM,
    validators: { onDynamic: goalFormSchema },
    onSubmit: ({ value }) => {
      if (!carePlanId) return;
      once(() => {
        addGoalFromForm(value, carePlanId);
        onClose();
      });
    },
    onSubmitInvalid: focusFirstInvalid,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Add Goal</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="mt-2 space-y-4"
        >
          <GoalFormFields form={form} conditionOptions={conditionOptions} />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton disabled={!carePlanId}>Add goal</form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
